import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { prepareChartData } from '../Sos2/prepareChartData';   // REUSED: DER -> coloured nodes
import { METRIC_LABELS } from '../Sos2/Constants';               // axis labels for the colour-by dropdown
import { WikiDrawer } from '../Sos2/InfoUI';                     // REUSED: full-article side drawer
import { wikiArticle } from '../Sos2/wikiContent';
import { lensStatus, bandWhy } from '../Sos2/verdictModel';      // REUSED: the triage gate's own reasoning
import { resolveLrUnit } from '../Sos2/deepLink';                 // REUSED: writer-visible unit names -> node
import SunburstLegend from '../Sos2/SunburstLegend';             // REUSED: the colour key, so it cannot drift
import StripView from './StripView';
import MapView from './MapView';
import DetailModal from './DetailModal';
import PatternsModal from './PatternsModal';

// Which display config the caller can set. `grains` picks which rows show
// (e.g. ['scene'] = a thin strip of scenes; ['sequence','scene'] = two rows).
// `metric` is the colour-by criterion (any Sos2 METRIC_KEYS key: 'overall',
// a lens like 'design'/'execution'/'read', or an axis like 'pressure').
const DEFAULT_CONFIG = {
  grains: ['scene'],
  metric: 'overall',
  dimNotOwed: true,
  selectable: true,
  cellSizing: 'weight',   // 'weight' = width by page count, 'equal' = equal width
  showAxisPicker: false,  // show a "Colour by" dropdown above the strips
  axisChoices: null,      // e.g. ['unpredictability','freshness'] -> dropdown holds ONLY these
  showAllScores: false,   // detail pane: default the "show all scores (incl. not owed)" toggle ON
};

// Axis keys the colour-by dropdown offers by default. Callers can pass config.axisChoices
// to shrink this to any subset -- e.g. ['unpredictability','freshness'] for the article helper.
// The three LENSES sit between Triage and the twelve axes: Triage is the gate's verdict, a lens is
// one of the three readings the gate is computed FROM, and an axis is one of the twelve underneath.
// Offering the lenses answers the question the gate raises but does not show — "which of the three
// is the broken one?" — without making anyone pick among twelve terms first.
const LENS_CHOICES = ['design', 'execution', 'read'];

const DEFAULT_AXIS_CHOICES = [
  'overall', ...LENS_CHOICES,
  'contract', 'pressure', 'development', 'movement', 'consequence',
  'economy', 'assembly', 'clarity', 'freshness', 'unpredictability', 'charge', 'grip',
];

// Lens grouping for the colour-by dropdown (matches the app's Design/Execution/Read).
const LENSES = [
  { label: 'Design', keys: ['contract', 'pressure', 'development', 'movement', 'consequence'] },
  { label: 'Execution', keys: ['economy', 'assembly', 'clarity'] },
  { label: 'Read', keys: ['freshness', 'unpredictability', 'charge', 'grip'] },
];

// Plain-language meaning for each metric (shown in the (i) tooltip).
//
// SOURCE OF TRUTH: eval/prompts/der_unit_grader_v0_1.txt:43-74 — the text the
// grader model actually receives. Register follows eval/axis_plain.php (no
// jargon: no "grain", "thread", "unit-type", "mechanism", "axis").
//
// Corrected 2026-08-13. Four entries previously contradicted the grader rather
// than simplifying it, and two of those crossed a documented firewall:
//   pressure — was "Does tension build, or go slack?" Tension is the FELT
//     effect, which the grader assigns to charge. Inverted the pressure/charge
//     firewall (der_unit_grader_v0_1.txt:46, :72).
//   charge   — "dramatic voltage" leaked back across the same firewall.
//   development — was "Does something change...", which is movement's question.
//     The grader defines development as DEEPENS, "not merely a changed result"
//     (:49). Post-PC-01/MD firewall patch.
//   contract — was "promising and what is at stake", a different axis entirely.
//     The grader defines it as a determinate governing thread (:43).
//   grip     — was "hold attention", i.e. sustained. Grip is exit-local: pull
//     into the NEXT unit at the boundary (:73).
// If the grader prompt changes, change these with it.
const METRIC_DESC = {
  // 'overall' is labelled "Triage" everywhere in the UI (Constants.js:12) and is NOT a score.
  // It is a gate: each of Design / Execution / Read is Solid, Soft or Broken, and the band is the
  // most optimistic call none of them vetoes (verdictModel.js:1-16). Corrected 2026-08-13 — this
  // previously read "the overall reading across all twelve craft qualities", which was wrong twice:
  // the colour comes from the 3-lens gate, and the fallback number behind the key is `execution`
  // (economy + assembly + clarity), not twelve axes.
  // The three lenses. Wording follows the gate's own definition (verdictModel.js:1-16): each lens is
  // rated solid / soft / broken and ONE broken lens sets the band on its own, which is the thing a
  // reader most wants to check after seeing a Triage colour they did not expect.
  design: 'Design — is the unit built to work at all? Its thread, the pressure on it, whether it develops, turns, and obliges what comes next.',
  execution: 'Execution — is it built well on the page? Waste, order and pacing, and whether the reader can follow what matters.',
  read: 'Read — what it is actually like to read. Freshness, how predictable it is, whether a feeling comes off the page, and whether the exit pulls you on.',
  overall: 'Triage — how much work a unit needs: Keep, Polish, Rework, or Rebuild. Not an average. Design, Execution and Involvement are each rated solid, soft or broken, and one broken group sets the level on its own.',
  contract: 'Is the unit built around one thing it is actually about — and does it hold to it?',
  pressure: 'Is something on the page forcing the choices — a person, deadline, threat, or rule that can exact a cost? (Mood alone is not pressure.)',
  development: 'Does the situation deepen — a new tactic, complication, or piece of understanding — or does it restate the same note?',
  movement: 'Does it end in a genuinely different condition than it began, caused by something inside the unit itself?',
  consequence: 'Does the result oblige what comes next — a debt, threat, or constraint — or does it reset?',
  economy: 'Does every beat and line earn its place, or is there avoidable waste?',
  assembly: 'Are the parts ordered, paced, and joined so they build — or do they just sit side by side?',
  clarity: 'Can the reader follow what matters? (Withholding something on purpose does not count against it.)',
  freshness: 'Is this a fresh take on this kind of scene, or the version we have seen many times?',
  unpredictability: 'Can you call the beats before they land — and when it surprises, is the surprise fairly set up?',
  charge: 'Does a feeling actually come off the page — evoked by the beats, not just stated?',
  grip: 'At the exit, are you pulled into the next one — or does it settle?',
};

// Which axis to open on when the strip is coloured by Triage or a lens — i.e. when the click did
// not already name an axis. NOT "the lowest of the twelve": raw cross-axis ranking is forbidden
// because the axes are near-collinear and one cell's uncertainty exceeds the whole map's range
// (docs/CODEBASE_MAP.md §13.14 / landmine 58). Instead follow the gate's own reasoning, which is
// the same chain the deterministic repair note uses (eval/run_stage0.php stage0_der_repair):
//
//   1. which LENS vetoed the band (broken first, then soft) — a within-lens comparison
//   2. within that lens, the axis the unit actually OWES (core/expected) and scores lowest
//
// Returns { axis, why } or null. `why` is written for someone who has never seen this interface:
// it states the gate's finding, then frames the axis as a starting point rather than a verdict —
// these are produced screenplays on a landing page, and "here is what is worst about Amélie" is
// not the impression to lead with.
const OWED_OBLIGATIONS = new Set(['core', 'expected']);
const NOT_SCORED = new Set(['not_owed', 'na', 'not_relevant', 'not_applicable']);
// Lens sizes are 5 / 3 / 4; spelled out because the sentence reads better than a digit here.
const COUNT_WORD = { 3: 'three', 4: 'four', 5: 'five' };

function suggestAxis(dc) {
  if (!dc) return null;
  const statuses = {
    design: lensStatus(dc.design),
    execution: lensStatus(dc.execution),
    read: lensStatus(dc.read),
  };
  const LENS_OF = { Design: 'design', Execution: 'execution', Read: 'read' };

  // The lens that set the level: a broken one if there is one, otherwise a soft one.
  const pick = (want) => LENSES.find((g) => statuses[LENS_OF[g.label]] === want) || null;
  const weakest = pick('broken') || pick('soft');

  // Pick the owed axis within one lens, at whichever end we asked for. Deliberately never a
  // comparison across all twelve — only within a single lens, or across the three lens rollups,
  // both of which are far coarser than the per-axis noise floor (landmine 58).
  const extreme = (group, dir) => {
    let best = null;
    for (const key of group.keys) {
      const cell = dc.scores?.[key];
      if (!cell || typeof cell !== 'object') continue;
      const score = Number(cell.score);
      if (!Number.isFinite(score)) continue;
      if (NOT_SCORED.has(cell.status)) continue;
      if (cell.obligation && !OWED_OBLIGATIONS.has(cell.obligation)) continue;
      if (!best || (dir === 'low' ? score < best.score : score > best.score)) best = { axis: key, score };
    }
    return best;
  };

  if (weakest) {
    const best = extreme(weakest, 'low');
    if (!best) return null;
    const state = statuses[LENS_OF[weakest.label]];   // 'broken' | 'soft'
    return {
      axis: best.axis,
      why: {
        lens: weakest.label,
        // Names the LENS, not the axis — the axis is visibly selected in the rail underneath, so
        // repeating it here spent a sentence on something already on screen. Pointing at the rail
        // teaches the structure (lens → its axes) instead of just handing over one answer.
        text: `${weakest.label} is the ${state} lens here. Its ${COUNT_WORD[weakest.keys.length] || weakest.keys.length} axes are below — open one to see what the scene owes and how to strengthen it.`,
      },
    };
  }

  // ALL THREE LENSES SOLID. There is no problem to lead with, and on the produced screenplays this
  // landing page shows, that is the common case — leaving it to the empty "Pick an axis" pane meant
  // most clicks still paid out nothing. Lead with what the unit does BEST instead, which is both
  // honest and the right register for a demo on work the visitor already admires.
  const strongestLens = [...LENSES]
    .filter((g) => Number.isFinite(Number(dc[LENS_OF[g.label]])))
    .sort((a, b) => Number(dc[LENS_OF[b.label]]) - Number(dc[LENS_OF[a.label]]))[0];
  if (!strongestLens) return null;
  const top = extreme(strongestLens, 'high');
  if (!top) return null;
  return {
    axis: top.axis,
    why: {
      lens: strongestLens.label,
      text: `All three lenses hold here. ${METRIC_LABELS[top.axis] || top.axis} is where this unit reads strongest — open any axis to see why.`,
    },
  };
}

// The full guide behind a metric. 'overall' is Triage, which is a gate rather than an axis and so
// has its own overview article; everything else is one of the twelve axes. Returns null when no
// article exists, and the tooltip then simply omits the "Open guide" affordance.
function articleForMetric(metric) {
  if (metric === 'overall') return wikiArticle('overview', 'triage');
  // Lenses have their own wiki kind; asking for kind 'axis' with a lens id returns null and the
  // "Open guide →" affordance silently disappears.
  if (LENS_CHOICES.includes(metric)) return wikiArticle('lens', metric);
  return wikiArticle('axis', metric);
}

// Hover for the one-line meaning, click through for the full article — the same two-step the Sos2
// app uses (InfoUI.js InfoDot -> WikiDrawer). Previously hover-only, which left "Triage" as a term
// the page used prominently and never explained beyond a sentence.
function InfoTip({ text, article, onOpen }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const canOpen = !!(article && onOpen);
  return (
    <span className="lrs-info-wrap" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" className="lrs-info"
        aria-label={canOpen ? `What is ${article.label}?` : 'What this means'}
        onClick={() => { if (canOpen) { setOpen(false); onOpen(article); } else { setOpen((o) => !o); } }}>i</button>
      {open ? (
        <span className="lrs-info-pop">
          {text}
          {canOpen ? (
            <button type="button" className="lrs-info-more"
              onClick={(e) => { e.stopPropagation(); setOpen(false); onOpen(article); }}>Open guide →</button>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

export default function LRSnapshot({ hash, initialConfig = {}, apiRef }) {
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG, ...initialConfig });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);   // a node's detailedContent
  const [wiki, setWiki] = useState(null);           // full-guide article shown in WikiDrawer
  const [patternsOpen, setPatternsOpen] = useState(false);
  const [marked, setMarked] = useState(null);       // { ids: Set, name, count, grain } — a pattern shown on the strip
  useEffect(() => { setMarked(null); setPatternsOpen(false); }, [hash]);

  // Tracking hook (2026-10-24): the widget only ANNOUNCES interactions as a window event; the host page
  // decides whether and how to record them (the homepage relays them to its Landing LR Demo events).
  // Never throws: tracking must not break the demo.
  const filmRef = React.useRef(null);
  const announce = useCallback((name, props = {}) => {
    try {
      window.dispatchEvent(new CustomEvent('lrsnapshot:track',
        { detail: { name, props: { film: filmRef.current, ...props } } }));
    } catch (_) {}
  }, []);
  const shownHashes = React.useRef([]);

  // ── PHONE LAYOUT (2026-09-18) ────────────────────────────────────────────────────────────────
  // Below 900px — the same breakpoint at which the strip's CSS compresses every row to the
  // viewport — the strip stops being a usable set of tap targets (unlabelled 8px sequence cells,
  // an unhittable scene row). So on narrow viewports the strip stays as a NON-interactive
  // minimap and MapView below it becomes the thing you touch: act → sequence → scene, top to
  // bottom, expanding in flow. Decided by viewport, never by user agent, so a narrow desktop
  // window behaves like a phone and a wide tablet like a desktop.
  const [narrow, setNarrow] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 900px)').matches : false
  ));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 900px)');
    const onChange = (e) => setNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
  }, []);

  // Expose imperative setConfig so the caller can change display while loaded (no refetch).
  useEffect(() => {
    if (apiRef) apiRef.setConfig = (patch) => setConfig((c) => ({ ...c, ...patch }));
  }, [apiRef]);

  // Fetch ALL of the script's DER once, by hash. Everything else is client-side.
  useEffect(() => {
    if (!hash) { setError('missing hash'); return; }
    let alive = true;
    setData(null); setError(null);
    fetch(`/lrsnapshot/data?hash=${encodeURIComponent(hash)}`, { headers: { Accept: 'application/json' } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => {
        if (!alive) return;
        setData(d);
        filmRef.current = (d && d.movie && d.movie.title) || null;
        // The first script shown is the page's default; every later one is a visitor's switch.
        if (shownHashes.current.length) announce('film_switched', { from_count: shownHashes.current.length });
        shownHashes.current.push(hash);
      })
      .catch((e) => { if (alive) setError(String(e.message || e)); });
    return () => { alive = false; };
  }, [hash]);

  const includeScenes = (config.grains || []).includes('scene');

  // Recompute coloured nodes whenever the data OR the colour-by/dim settings change.
  // prepareChartData already encodes owedness + the grade/band gradient, so the strip
  // matches the main app exactly.
  const chart = useMemo(() => {
    if (!data) return null;
    return prepareChartData(data, includeScenes, config.metric, config.dimNotOwed);
  }, [data, includeScenes, config.metric, config.dimNotOwed]);

  // Colour-by dropdown options. When axisChoices is set, the dropdown holds ONLY those
  // axes; otherwise the full default set. The picker shows when explicitly enabled OR
  // whenever a restricted axisChoices list is supplied.
  const axisOptions = useMemo(() => (
    Array.isArray(config.axisChoices) && config.axisChoices.length
      ? config.axisChoices
      : DEFAULT_AXIS_CHOICES
  ), [config.axisChoices]);
  const showPicker = config.showAxisPicker || (Array.isArray(config.axisChoices) && config.axisChoices.length > 0);
  // If the active metric isn't one of the offered choices (e.g. a restricted dropdown), snap to the first.
  useEffect(() => {
    if (showPicker && !axisOptions.includes(config.metric)) {
      setConfig((c) => ({ ...c, metric: axisOptions[0] }));
    }
  }, [showPicker, axisOptions, config.metric]);

  const onSelect = useCallback((node) => {
    if (config.selectable === false) return;
    const dc = node?.detailedContent;
    if (!dc) { setSelected(null); return; }
    announce('unit_opened', { grain: node.nodeType || null, metric: config.metric });
    // Open the detail modal spotlighting the axis the strip is currently coloured by, so clicking a
    // unit in (say) the Assembly view lands on Assembly. NodePanel seeds its spotlight from
    // node.focusAxis.
    const AXES = LENSES.flatMap((g) => g.keys);
    const focusAxis = AXES.includes(config.metric) ? config.metric : null;
    if (focusAxis) { setSelected({ ...dc, focusAxis }); return; }

    // Triage and lens views used to open NEUTRAL — an empty right pane reading "Pick an axis on the
    // left". That is the wrong answer to the question the click asked ("why is this block this
    // colour?"), and it asks a first-time visitor to choose between twelve terms the page has not
    // taught yet. So suggest one, and say why we suggested it.
    const suggestion = suggestAxis(dc);
    setSelected(suggestion
      ? { ...dc, focusAxis: suggestion.axis, focusAxisSuggested: suggestion.why }
      : dc);
  }, [config.selectable, config.metric, announce]);

  // Deep link into one unit: config.openUnit ('scene-8', 'act-2b', …, the same grammar as ?lr= on
  // /view_analysis). Opens that unit's reading once this script's data is in. Added 2026-10-22 for the
  // homepage video's "Try it on Green Book →", which continues where the video leaves off (scene 8).
  // Keyed by hash+unit so it opens once per request, not on every re-render; the landing script clears
  // openUnit when the visitor switches film, so another script never opens on a unit meant for this one.
  const openedRef = React.useRef(null);
  useEffect(() => {
    const want = config.openUnit;
    if (!want || !chart) return;
    const key = `${hash}|${want}`;
    if (openedRef.current === key) return;
    const node = resolveLrUnit(chart.nodes, want);
    if (node) { openedRef.current = key; onSelect(node); }
  }, [config.openUnit, chart, hash, onSelect]);

  // Patterns (the app's Patterns tab: rollups[grain].habits). Count only the recurring problems the
  // button promises; "protect" and smaller signals are inside.
  const patternCount = data && data.rollups
    ? ['scene', 'sequence', 'act'].reduce((n, g) => n + (((data.rollups[g] || {}).habits) || []).length, 0) : 0;
  const nodeForUnit = (u) => (chart && u ? (resolveLrUnit(chart.nodes, u.id || u.short_label)
    || chart.nodes.find((n) => u.ref && (n.unitId === u.ref || n.detailedContent?.unit_id === u.ref)) || null) : null);
  const showUnits = (units, name) => {
    const nodes = (units || []).map(nodeForUnit).filter(Boolean);
    if (!nodes.length) return;
    const grain = (units[0] && units[0].grain) || nodes[0].nodeType;
    setMarked({ ids: new Set(nodes.map((n) => n.id)), name: name || 'this pattern', count: nodes.length, grain });
    announce('pattern_opened', { grain, units: nodes.length });
    // The modal stays open: RollupPanel calls this as soon as a pattern card OPENS (in the app the
    // timeline sits beside it). The strip behind is marked; the modal's "on the timeline" links
    // and its bar close it to show the marks (PatternsModal).
  };
  const selectUnit = (u) => {
    const n = nodeForUnit(u);
    if (n) { setPatternsOpen(false); onSelect(n); }
  };

  if (error) return <div className="lrs-error">Layered Read snapshot unavailable.</div>;
  if (!data || !chart) return <div className="lrs-loading">Loading…</div>;

  return (
    <div className="lrs-root">
      {/* ── Colour-encoding row: the dropdown AND its key on ONE line. ──────────────────────────
          Matches the Layered Read page, where "Colour by <select> (i)" and the legend are siblings
          in one flex row (Icicle.js:1500-1511) — owner, 2026-08-28: "we should do like in LR.
          drop down and legen on same line instead of two". They are one decision (what is painting
          the map, and what the paint means), so they read as one control strip; stacked, they
          looked like two unrelated blocks and cost a line of vertical space above the chart, which
          is the space this section can least afford.

          ⚠️ ONE control, not a display AND a control. A "COLOURED BY / Triage" head used to sit
          above this select — 10.5px uppercase grey over a 20px name, stating exactly what the
          dropdown already stated. Two failures at once: an eyebrow (CLAUDE.md UI rule 6 bans the
          pattern, not just the small-grey styling), and the same word twice. The select IS the
          answer to "what is this coloured by", so the (i) explanation sits beside it.

          The legend renders whether or not the picker does — an embed with showAxisPicker off
          still needs its key. */}
      <div className="lrs-controls">
        {showPicker && (() => {
          const cur = axisOptions.includes(config.metric) ? config.metric : axisOptions[0];
          return (
            <div className="lrs-metric-pick">
              <label htmlFor="lrs-metric">Colour by</label>
              <select id="lrs-metric" value={cur} onChange={(e) => { const m = e.target.value; setConfig((c) => ({ ...c, metric: m })); announce('colour_changed', { metric: m }); }}>
                {axisOptions.includes('overall') ? <option value="overall">{METRIC_LABELS['overall'] || 'Overall'}</option> : null}
                {(() => {
                  const ls = LENS_CHOICES.filter((k) => axisOptions.includes(k));
                  return ls.length ? (
                    <optgroup label="Groups">
                      {ls.map((k) => <option key={k} value={k}>{METRIC_LABELS[k] || k}</option>)}
                    </optgroup>
                  ) : null;
                })()}
                {LENSES.map((g) => {
                  const keys = g.keys.filter((k) => axisOptions.includes(k));
                  return keys.length ? (
                    <optgroup key={g.label} label={g.label}>
                      {keys.map((k) => <option key={k} value={k}>{METRIC_LABELS[k] || k}</option>)}
                    </optgroup>
                  ) : null;
                })}
              </select>
              <InfoTip text={METRIC_DESC[cur]} article={articleForMetric(cur)} onOpen={setWiki} />
            </div>
          );
        })()}
        {/* REUSED from the Sos2 app rather than written here, so the swatches and the wording
            cannot drift from the chart they describe — the labels are load-bearing ("Two lenses
            low", not "Cut"; a scene graded in isolation cannot license a cut, see
            verdictModel.bandLabel). surface="timeline" drops the sunburst's clockwise reading
            hint, and passing no onSwitchToTable drops the table link, which has nowhere to go on
            a landing page. It switches on `metric` itself, so it stays correct when the visitor
            changes the dropdown to a single axis. */}
        <SunburstLegend metric={config.metric} surface="timeline" />
        {patternCount > 0 && config.showPatterns !== false ? (
          <button type="button" className="lrs-pat-btn" onClick={() => { setPatternsOpen(true); announce('patterns_opened', { patterns: patternCount }); }}>
            {patternCount} {patternCount === 1 ? 'pattern' : 'patterns'}
          </button>
        ) : null}
      </div>
      {marked ? (
        <div className="lrs-marked" role="status">
          <span><b>{marked.count} {marked.count === 1 ? (marked.grain === 'act' ? 'act' : marked.grain === 'sequence' ? 'sequence' : 'scene') : (marked.grain === 'act' ? 'acts' : marked.grain === 'sequence' ? 'sequences' : 'scenes')}</b> with this pattern: {marked.name}</span>
          <button type="button" onClick={() => setPatternsOpen(true)}>Back to patterns</button>
          <button type="button" onClick={() => setMarked(null)}>Clear</button>
        </div>
      ) : null}
      <StripView
        nodes={chart.nodes}
        grains={config.grains}
        sizing={config.cellSizing}
        onSelect={onSelect}
        movie={data}
        metric={config.metric}
        interactive={!narrow}
        marked={marked ? marked.ids : null}
      />
      {narrow && config.selectable !== false ? (
        <MapView nodes={chart.nodes} movie={data} metric={config.metric} onSelect={onSelect} />
      ) : null}
      {patternsOpen && (
        <PatternsModal rollups={data.rollups} script={data.movie} title={data.movie && data.movie.title}
                       marked={marked} onClose={() => setPatternsOpen(false)} onShowUnits={showUnits} onSelectUnit={selectUnit} />
      )}
      {selected && (
        <DetailModal
          node={selected}
          peers={peersFor(chart.nodes, selected)}
          movieHash={data.hash}
          /* The script row — Icicle passes this to NodePanel as `script`; the snapshot never did,
             so grain wording (scene/beat, per script_type) fell back to defaults in the modal. */
          script={data.movie}
          canGenerate={!!data.viewer_is_owner}
          showAllScores={!!config.showAllScores}
          onClose={() => setSelected(null)}
        />
      )}
      {/* Portals to <body> at z-index 2000, so it clears the site navbar and the landing-page
          stacking contexts. onNavigate lets the drawer's framework index hop between articles
          in place, the same as in the Sos2 app. */}
      <WikiDrawer article={wiki} onClose={() => setWiki(null)} onNavigate={setWiki} />
    </div>
  );
}

// Within-script peers of the selected unit's grain, for NodePanel's percentile context.
function peersFor(nodes, sel) {
  const grain = sel?.type; // 'scene' | 'sequence' | 'act' | 'movie'
  return nodes
    .filter((n) => n.nodeType === grain && n.detailedContent && n.detailedContent.scores)
    .map((n) => n.detailedContent.scores);
}
